export type CustomsTerritoryId = 'MELILLA';

export type CustomsTerritoryShipper = 'UPS' | 'DHL';

export type CustomsTerritory = {
    id: CustomsTerritoryId;
    /** ISO country code stored on the order (parent country). */
    countryCode: string;
    /** Normalized city names (lowercase, no diacritics). */
    cityNames: readonly string[];
    /** Postal code prefixes belonging to the territory. */
    postalCodePrefixes: readonly string[];
    /** Country code expected by a specific carrier instead of countryCode. */
    shipperCountryCodes: Readonly<Partial<Record<CustomsTerritoryShipper, string>>>;
};

export type CustomsTerritoryAddress = {
    countryCode?: string | null;
    cityName?: string | null;
    postalCode?: string | null;
};

export const CUSTOMS_TERRITORIES: readonly CustomsTerritory[] = [
    {
        id: 'MELILLA',
        countryCode: 'ES',
        cityNames: ['melilla'],
        postalCodePrefixes: ['52'],
        shipperCountryCodes: { UPS: 'XL' }
    }
] as const;

const normalizeCityName = (cityName: string): string =>
    cityName
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const normalizeCountryCode = (countryCode: string): string => countryCode.trim().toUpperCase();

const normalizePostalCode = (postalCode: string): string => postalCode.replace(/\s/g, '');

const matchesTerritory = (territory: CustomsTerritory, address: CustomsTerritoryAddress): boolean => {
    const countryCode = address.countryCode ? normalizeCountryCode(address.countryCode) : '';
    if (countryCode !== territory.countryCode) {
        return false;
    }

    const cityName = address.cityName ? normalizeCityName(address.cityName) : '';
    if (cityName && territory.cityNames.includes(cityName)) {
        return true;
    }

    const postalCode = address.postalCode ? normalizePostalCode(address.postalCode) : '';
    if (postalCode && territory.postalCodePrefixes.some((prefix) => postalCode.startsWith(prefix))) {
        return true;
    }

    return false;
};

export const findCustomsTerritory = (address: CustomsTerritoryAddress): CustomsTerritory | undefined =>
    CUSTOMS_TERRITORIES.find((territory) => matchesTerritory(territory, address));

export const isCustomsTerritoryDestination = (address: CustomsTerritoryAddress): boolean =>
    findCustomsTerritory(address) !== undefined;

/** Returns the country code expected by the shipper, or the original country code. */
export const getShipperCountryCode = (shipper: string, address: CustomsTerritoryAddress): string => {
    const originalCountryCode = address.countryCode ? normalizeCountryCode(address.countryCode) : '';
    const territory = findCustomsTerritory(address);
    if (!territory) {
        return originalCountryCode;
    }

    const shipperKey = shipper.trim().toUpperCase() as CustomsTerritoryShipper;
    return territory.shipperCountryCodes[shipperKey] ?? originalCountryCode;
};
